// btw-front/src/pages/AdminSalesReps.jsx
//
// 관리자 영업대표 관리 — N행 추가/수정/삭제. 딜 등록 시 셀렉트 소스로 사용.
// 파트너 측 PartnerEdit 의 RepSection 패턴을 단순화해 옮긴 형태.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message, Modal } from "antd";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import {
  listSalesReps,
  createSalesRep,
  updateSalesRep,
  deleteSalesRep,
} from "../api/adminSalesReps";
import { formatPhone } from "../lib/format";

function emptyRep() {
  return { name: "", title: "", phone: "", email: "" };
}

export default function AdminSalesReps() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // 편집 중인 행 id. null = 추가 모드(form 만 노출)
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyRep);
  const [saving, setSaving] = useState(false);

  function reload() {
    setLoading(true);
    listSalesReps()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }
  useEffect(reload, []);

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }
  function startEdit(row) {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      title: row.title || "",
      phone: row.phone || "",
      email: row.email || "",
    });
  }
  function cancelEdit() {
    setEditingId(null);
    setForm(emptyRep());
  }

  async function handleSave() {
    if (!form.name.trim()) {
      message.warning(t("partners.signup.errors.required"));
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        title: form.title.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
      };
      if (editingId) {
        await updateSalesRep(editingId, payload);
        message.success(t("admin.salesReps.updated"));
      } else {
        await createSalesRep(payload);
        message.success(t("admin.salesReps.added"));
      }
      setEditingId(null);
      setForm(emptyRep());
      reload();
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        t("admin.salesReps.saveFailed");
      message.error(msg);
    } finally {
      setSaving(false);
    }
  }

  function handleDelete(row) {
    Modal.confirm({
      title: t("admin.salesReps.deleteConfirmTitle"),
      content: t("admin.salesReps.deleteConfirmContent", { name: row.name }),
      okText: t("admin.salesReps.deleteConfirmOk"),
      cancelText: t("admin.salesReps.deleteConfirmCancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteSalesRep(row.id);
          message.success(t("admin.salesReps.deleted"));
          if (editingId === row.id) cancelEdit();
          reload();
        } catch (err) {
          const msg =
            (err && err.payload && err.payload.error && err.payload.error.message) ||
            t("admin.salesReps.deleteFailed");
          message.error(msg);
        }
      },
    });
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <div className="mx-auto max-w-4xl">
            <header className="flex flex-wrap items-end justify-between gap-3">
              <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
                {t("admin.salesReps.title")}
              </h1>
              <Link to="/admin" className="btw-btn-ghost">
                ← {t("admin.salesReps.backToAdmin")}
              </Link>
            </header>
            <p className="mt-2 text-sm text-slate-500">{t("admin.salesReps.hint")}</p>

            {/* 추가/수정 폼 */}
            <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
              <h2 className="text-sm font-semibold text-slate-800">
                {editingId ? t("admin.salesReps.editTitle") : t("admin.salesReps.addTitle")}
              </h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Input
                  label={t("admin.salesReps.name")}
                  required
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  maxLength={100}
                />
                <Input
                  label={t("admin.salesReps.position")}
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  maxLength={100}
                />
                <Input
                  label={t("admin.salesReps.phone")}
                  type="tel"
                  inputMode="numeric"
                  value={form.phone}
                  onChange={(e) => setField("phone", formatPhone(e.target.value))}
                  maxLength={13}
                />
                <Input
                  label={t("admin.salesReps.email")}
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  maxLength={190}
                />
              </div>
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                {editingId && (
                  <button type="button" className="btw-btn-ghost" onClick={cancelEdit}>
                    {t("admin.salesReps.cancel")}
                  </button>
                )}
                <Button variant="accent" onClick={handleSave} disabled={saving}>
                  {editingId ? t("admin.salesReps.save") : t("admin.salesReps.add")}
                </Button>
              </div>
            </section>

            {/* 목록 */}
            <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
              {loading ? (
                <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
              ) : items.length === 0 ? (
                <EmptyState title={t("admin.salesReps.empty")} />
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">
                        {t("admin.salesReps.name")}
                      </th>
                      <th className="px-4 py-3 text-left font-medium">
                        {t("admin.salesReps.position")}
                      </th>
                      <th className="px-4 py-3 text-left font-medium">
                        {t("admin.salesReps.phone")}
                      </th>
                      <th className="px-4 py-3 text-left font-medium">
                        {t("admin.salesReps.email")}
                      </th>
                      <th className="px-4 py-3 text-right font-medium">
                        {t("admin.salesReps.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((r) => (
                      <tr key={r.id}>
                        <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                        <td className="px-4 py-3 text-slate-700">{r.title || "-"}</td>
                        <td className="px-4 py-3 text-slate-700">{r.phone || "-"}</td>
                        <td className="px-4 py-3 text-slate-700">{r.email || "-"}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-400"
                              onClick={() => startEdit(r)}
                            >
                              {t("admin.salesReps.edit")}
                            </button>
                            <button
                              type="button"
                              className="rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-600 hover:border-rose-400 hover:bg-rose-50"
                              onClick={() => handleDelete(r)}
                            >
                              {t("admin.salesReps.delete")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}
